import { Button } from "@/components/ui/button";
import { Combobox } from "@/components/ui/combobox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { getProductFormatFullLabel, getProductFormatShortCode } from "@/utils/product-format-display";
import {
    CoachFundingBasketPayload,
    CoachFunderRow,
    CoachFunderTheme,
    SmartCatalogOptions,
    SmartCatalogOverridePayload
} from "@/services/walter-assistant";
import { FundingBasketItem, FundingBasketOverrideMode } from "@/types/content/funding_basket";

import {
    ChevronDown,
    ChevronLeft,
    ChevronRight,
    GripHorizontal,
    Pencil,
    ShoppingCart,
    SlidersHorizontal,
    X
} from "lucide-react";
import { useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { buildFunderListingUrl, buildThemeDetailUrl, formatConsumption, formatEnvelopeAmount } from "./funder-table.utils";
import { COACH_FUNDER_DRAWER_WIDTH, useDrawerLayout } from "../use-drawer-layout";

const FUNDER_DRAWER_HEIGHT_KEY = "coach-funder-drawer-height";
const FUNDER_DRAWER_LEFT_KEY = "coach-funder-drawer-left";

type EnvelopeOverrideInput = { funding_code: string; mode?: FundingBasketOverrideMode; amount?: number; reset?: boolean };
type RemoveItemInput = { product_code: string; funding_code: string };

type Props = {
    isOpen: boolean;
    onClose: () => void;
    payload: CoachFundingBasketPayload | null;
    isPending: boolean;
    options: SmartCatalogOptions | null;
    onOverrideProfile: (payload: SmartCatalogOverridePayload) => void;
    onRemoveItem: (input: RemoveItemInput) => void;
    onSetEnvelopeOverride: (input: EnvelopeOverrideInput) => void;
};

/**
 * Tableau financeur (drawer flottant). Grille compacte : une ligne par
 * financeur du playbook, avec colonnes Financeur | Enveloppe | Thèmes.
 * L'ajout au panier se fait depuis les pages catalogue CRM.
 */
export function FunderTableDrawer({
    isOpen,
    onClose,
    payload,
    isPending,
    options,
    onOverrideProfile,
    onRemoveItem,
    onSetEnvelopeOverride
}: Props) {
    const { height, left, isCollapsed, isDragging, toggleCollapsed, onLayoutHandlePointerDown } = useDrawerLayout({
        width: COACH_FUNDER_DRAWER_WIDTH,
        heightStorageKey: FUNDER_DRAWER_HEIGHT_KEY,
        leftStorageKey: FUNDER_DRAWER_LEFT_KEY
    });
    const [isProfileEditing, setIsProfileEditing] = useState(false);

    if (!isOpen) return null;

    const professionCode = payload?.profession_code ?? "";
    const professionName = options?.professions.find(p => p.code === professionCode)?.name ?? professionCode;
    const statusCode = payload?.professional_status ?? "";
    const statusName = options?.professional_statuses.find(s => s.code === statusCode)?.name ?? statusCode;

    return createPortal(
        <div
            className={cn(
                "fixed bottom-11 z-40 flex flex-col overflow-hidden rounded-t-md border border-b-0 bg-white shadow-lg",
                !isDragging && "transition-[height] duration-200 ease-out"
            )}
            style={{ height: isCollapsed ? 76 : height, left, width: COACH_FUNDER_DRAWER_WIDTH }}
            role="region"
            aria-label="Tableau financeur"
        >
            {/* drag handle */}
            <div
                className="flex shrink-0 cursor-move touch-none items-center justify-center border-b bg-gray-50 py-1"
                onPointerDown={onLayoutHandlePointerDown}
                aria-label="Déplacer et redimensionner le tableau financeur"
            >
                <GripHorizontal className="size-4 text-gray-400" aria-hidden />
            </div>

            {/* header — titre + profil compact + actions */}
            <div className="flex shrink-0 items-center gap-2 border-b px-3 py-1.5">
                <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold leading-tight text-gray-900">Tableau financeur</p>
                    {/* profil compact inline */}
                    <button
                        type="button"
                        onClick={() => setIsProfileEditing(v => !v)}
                        className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-800"
                        aria-label="Modifier le profil"
                    >
                        <span className="truncate">
                            {professionName || "Profession ?"} · {statusName || "Mode d'exercice ?"}
                        </span>
                        <Pencil className="size-2.5 shrink-0" aria-hidden />
                    </button>
                </div>
                <div className="flex shrink-0 items-center gap-0.5">
                    {isPending ? <span className="text-[10px] text-gray-400">Mise à jour…</span> : null}
                    <Button
                        variant="ghost" size="icon" className="size-7"
                        onClick={toggleCollapsed}
                        aria-label={isCollapsed ? "Développer" : "Réduire"}
                    >
                        <ChevronDown className={`size-3.5 transition-transform duration-200 ${isCollapsed ? "" : "rotate-180"}`} aria-hidden />
                    </Button>
                    <Button variant="ghost" size="icon" className="size-7" onClick={onClose} aria-label="Fermer">
                        <X className="size-3.5" aria-hidden />
                    </Button>
                </div>
            </div>

            {/* contenu — toujours rendu, masqué par overflow-hidden sur le conteneur */}
            {isProfileEditing && (
                <ProfileHeader
                    payload={payload}
                    options={options}
                    onOverrideProfile={update => {
                        onOverrideProfile(update);
                        const newProfession = update.profession_code ?? professionCode;
                        const newStatus = update.professional_status ?? statusCode;
                        if (newProfession && newStatus) setIsProfileEditing(false);
                    }}
                />
            )}
            <BasketPanel items={payload?.basket.items ?? []} funders={payload?.funders ?? []} onRemoveItem={onRemoveItem} />
            <ScrollArea className="min-h-0 flex-1">
                {!payload ? (
                    <EmptyState text="Aucun financeur configuré sur ce playbook." />
                ) : !payload.profile_ready ? (
                    <EmptyState text="Renseignez la profession et le mode d'exercice pour afficher les enveloppes." />
                ) : payload.funders.length === 0 ? (
                    <EmptyState text="Aucune enveloppe disponible pour ce profil." />
                ) : (
                    <FunderGrid
                        funders={payload.funders}
                        professionCode={professionCode}
                        basketItems={payload.basket.items}
                        onSetEnvelopeOverride={onSetEnvelopeOverride}
                    />
                )}
            </ScrollArea>
        </div>,
        document.body
    );
}

function EmptyState({ text }: { text: string }) {
    return (
        <div className="flex items-center justify-center p-6 text-center">
            <p className="text-sm text-muted-foreground">{text}</p>
        </div>
    );
}

/* -----------------------------------------------------------------------
 * Panier — tableau collapsible avec prix et lien catalogue
 * ----------------------------------------------------------------------- */

type BasketPanelProps = {
    items: FundingBasketItem[];
    funders: CoachFunderRow[];
    onRemoveItem: (input: RemoveItemInput) => void;
};

type BasketItemMeta = {
    price: number | null;
    catalogUrl: string | null;
    formatShort: string | null;
    formatLabel: string | null;
};

function resolveBasketItemMeta(item: FundingBasketItem, funders: CoachFunderRow[]): BasketItemMeta {
    // Prefer the metadata captured client-side at add-time (authoritative:
    // price / format / deep link come straight from the catalogue page).
    // Fall back to the matching funder theme card for legacy items added
    // before the metadata was persisted on the basket item.
    const funderRow = funders.find(f => f.funding_code.toUpperCase() === item.funding_code.toUpperCase());
    const theme = funderRow?.themes.find(t => t.top_product?.product_code === item.product_code);
    const fallbackPrice = theme?.top_product?.funding_price ?? theme?.top_product?.listing_price ?? null;

    const hasVirtualClass = Boolean(item.has_virtual_class);
    const formatShort = getProductFormatShortCode(item.product_format, hasVirtualClass);

    return {
        price: item.funding_price ?? item.listing_price ?? fallbackPrice,
        catalogUrl: item.crm_catalog_url ?? theme?.crm_catalog_url ?? null,
        formatShort,
        formatLabel: formatShort ? getProductFormatFullLabel(item.product_format, hasVirtualClass) : null,
    };
}

const PRICE_FORMATTER = new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
});

function BasketPanel({ items, funders, onRemoveItem }: BasketPanelProps) {
    const [open, setOpen] = useState(false);
    if (items.length === 0) return null;

    return (
        <div className="shrink-0 border-b">
            <button
                type="button"
                onClick={() => setOpen(v => !v)}
                className="flex w-full items-center gap-2 px-3 py-1.5 text-left hover:bg-gray-50"
            >
                <ShoppingCart className="size-3.5 shrink-0 text-primary" aria-hidden />
                <span className="flex-1 text-xs font-medium text-gray-700">
                    Panier — {items.length} formation{items.length > 1 ? "s" : ""}
                </span>
                <ChevronDown
                    className={`size-3.5 text-gray-400 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
                    aria-hidden
                />
            </button>
            {open && (
                <div className="px-3 pb-3">
                    <TooltipProvider delayDuration={150}>
                        <Table>
                            <TableHeader>
                                <TableRow className="text-[10px]">
                                    <TableHead className="h-7 py-0">Formation</TableHead>
                                    <TableHead className="h-7 py-0">Format</TableHead>
                                    <TableHead className="h-7 py-0">FO</TableHead>
                                    <TableHead className="h-7 py-0 text-right">Prix</TableHead>
                                    <TableHead className="h-7 py-0" />
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {items.map(item => {
                                    const { price, catalogUrl, formatShort, formatLabel } = resolveBasketItemMeta(item, funders);
                                    const productLabel = item.product_name || item.product_code;
                                    return (
                                        <TableRow key={`${item.product_code}-${item.funding_code}`} className="text-[11px]">
                                            <TableCell className="py-1.5 font-medium leading-tight">
                                                {catalogUrl ? (
                                                    <Link
                                                        to={catalogUrl}
                                                        className="text-gray-800 hover:text-primary hover:underline"
                                                        aria-label={`Voir ${productLabel} dans le catalogue`}
                                                    >
                                                        {productLabel}
                                                    </Link>
                                                ) : (
                                                    productLabel
                                                )}
                                            </TableCell>
                                            <TableCell className="py-1.5">
                                                {formatShort ? (
                                                    <Tooltip>
                                                        <TooltipTrigger asChild>
                                                            <span className="cursor-default rounded bg-gray-100 px-1 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-gray-600">
                                                                {formatShort}
                                                            </span>
                                                        </TooltipTrigger>
                                                        <TooltipContent>{formatLabel}</TooltipContent>
                                                    </Tooltip>
                                                ) : (
                                                    <span className="text-gray-300">—</span>
                                                )}
                                            </TableCell>
                                            <TableCell className="py-1.5">
                                                <span className="rounded bg-primary/10 px-1 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-primary">
                                                    {item.funding_code}
                                                </span>
                                            </TableCell>
                                            <TableCell className="py-1.5 text-right tabular-nums text-gray-600">
                                                {price !== null ? PRICE_FORMATTER.format(price) : "—"}
                                            </TableCell>
                                            <TableCell className="py-1.5">
                                                <div className="flex items-center justify-end">
                                                    <button
                                                        type="button"
                                                        onClick={() => onRemoveItem({ product_code: item.product_code, funding_code: item.funding_code })}
                                                        aria-label={`Retirer ${productLabel} du panier`}
                                                        className="rounded p-0.5 text-red-500 hover:bg-red-50 hover:text-red-600"
                                                    >
                                                        <X className="size-3" aria-hidden />
                                                    </button>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TooltipProvider>
                </div>
            )}
        </div>
    );
}

/* -----------------------------------------------------------------------
 * Profile header — comboboxes édition profession + mode d'exercice
 * ----------------------------------------------------------------------- */

type ProfileHeaderProps = {
    payload: CoachFundingBasketPayload | null;
    options: SmartCatalogOptions | null;
    onOverrideProfile: (payload: SmartCatalogOverridePayload) => void;
};

function ProfileHeader({ payload, options, onOverrideProfile }: ProfileHeaderProps) {
    const profession = payload?.profession_code ?? "";
    const status = payload?.professional_status ?? "";

    if (!options) return null;

    const profOptions = options.professions.map(p => ({ value: p.code, label: p.name }));
    const statusOptions = options.professional_statuses.map(s => ({ value: s.code, label: s.name }));

    return (
        <div className="flex shrink-0 flex-wrap items-end gap-2 border-b bg-blue-50/40 px-3 py-2">
            <div className="flex min-w-0 flex-1 basis-44 flex-col gap-0.5">
                <label className="text-xs font-medium text-gray-600">Profession</label>
                <Combobox
                    name="funder-profession"
                    options={profOptions}
                    value={profession}
                    placeholder="Sélectionner…"
                    commandPlaceholder="Rechercher une profession…"
                    className="h-8 w-full text-sm"
                    onValueChange={code => onOverrideProfile({ profession_code: code })}
                />
            </div>
            <div className="flex min-w-0 flex-1 basis-44 flex-col gap-0.5">
                <label className="text-xs font-medium text-gray-600">Mode d&apos;exercice</label>
                <Combobox
                    name="funder-status"
                    options={statusOptions}
                    value={status}
                    placeholder="Sélectionner…"
                    commandPlaceholder="Rechercher un mode…"
                    className="h-8 w-full text-sm"
                    onValueChange={code => onOverrideProfile({ professional_status: code })}
                />
            </div>
        </div>
    );
}

/* -----------------------------------------------------------------------
 * Funder grid — grille CSS à 3 colonnes, 1 ligne par financeur
 * -----------------------------------------------------------------------
 * Colonnes : 140px Financeur | 150px Enveloppe | 1fr Thèmes
 * ----------------------------------------------------------------------- */

type FunderGridProps = {
    funders: CoachFunderRow[];
    professionCode: string;
    basketItems: FundingBasketItem[];
    onSetEnvelopeOverride: (input: EnvelopeOverrideInput) => void;
};

const COL_TEMPLATE = "140px 150px 1fr";

function FunderGrid({ funders, professionCode, basketItems, onSetEnvelopeOverride }: FunderGridProps) {
    return (
        <div className="min-w-0">
            <div
                className="sticky top-0 z-10 grid border-b bg-gray-50 px-3 py-1 text-[10px] font-semibold uppercase tracking-wide text-gray-400"
                style={{ gridTemplateColumns: COL_TEMPLATE }}
            >
                <span>Financeur</span>
                <span>Enveloppe</span>
                <span>Thèmes</span>
            </div>

            {funders.map((row, idx) => (
                <FunderRow
                    key={row.funding_code}
                    row={row}
                    professionCode={professionCode}
                    basketItems={basketItems}
                    onSetEnvelopeOverride={onSetEnvelopeOverride}
                    isLast={idx === funders.length - 1}
                />
            ))}
        </div>
    );
}

type FunderRowProps = {
    row: CoachFunderRow;
    professionCode: string;
    basketItems: FundingBasketItem[];
    onSetEnvelopeOverride: (input: EnvelopeOverrideInput) => void;
    isLast: boolean;
};

function FunderRow({ row, professionCode, basketItems, onSetEnvelopeOverride, isLast }: FunderRowProps) {
    const notEligible = row.source === "not_eligible";

    return (
        <div
            className={cn(
                "grid items-start px-3",
                !isLast && "border-b border-gray-100",
                notEligible ? "bg-gray-50 opacity-60" : row.below_threshold ? "bg-amber-50/40" : "bg-white"
            )}
            style={{ gridTemplateColumns: COL_TEMPLATE }}
            role="row"
            aria-label={`Financeur ${row.funding_label}`}
        >
            {/* col 1 — Financeur */}
            <div className="flex min-w-0 flex-col justify-center gap-0.5 py-2.5 pr-2">
                <span className={cn("truncate text-sm font-semibold leading-tight", notEligible ? "text-gray-400" : "text-gray-900")}>
                    {row.funding_label}
                </span>
                {notEligible ? (
                    <span className="text-[9px] text-gray-400">Non éligible</span>
                ) : row.is_doubled ? (
                    <span className="text-[9px] text-blue-500">×2 sync</span>
                ) : null}
            </div>

            {/* col 2 — Enveloppe */}
            <div className="flex min-w-0 items-center gap-1 py-2.5 pr-2">
                {notEligible ? (
                    <span className="text-xs text-gray-300">—</span>
                ) : (
                    <>
                        <div className="min-w-0 flex-1">
                            <p className={cn("text-sm font-semibold tabular-nums leading-tight", row.below_threshold ? "text-amber-700" : "text-gray-900")}>
                                {formatEnvelopeAmount(row.remaining, row.unit)}
                            </p>
                            <MiniGauge row={row} />
                        </div>
                        <EnvelopeOverridePopover row={row} onSetEnvelopeOverride={onSetEnvelopeOverride} />
                    </>
                )}
            </div>

            {/* col 3 — Thèmes */}
            <div className="min-w-0 py-2.5">
                {notEligible ? (
                    <span className="text-xs text-gray-300">—</span>
                ) : (
                    <ThemeCell themes={row.themes} fundingCode={row.funding_code} professionCode={professionCode} row={row} basketItems={basketItems} />
                )}
            </div>
        </div>
    );
}

/* Mini barre de progression enveloppe */
function MiniGauge({ row }: { row: CoachFunderRow }) {
    const annual = row.annual_amount ?? 0;
    if (annual <= 0) return null;
    const consumedPct = Math.min(((row.consumed_pre_call ?? 0) / annual) * 100, 100);
    const basketPct = Math.min(((row.basket_consumption ?? 0) / annual) * 100, 100 - consumedPct);
    const remainingPct = Math.max(100 - consumedPct - basketPct, 0);

    return (
        <div className="mt-1 flex h-1.5 w-full overflow-hidden rounded-full bg-gray-100">
            <div className="bg-slate-300" style={{ width: `${consumedPct}%` }} />
            <div className="bg-primary/70" style={{ width: `${basketPct}%` }} />
            <div className={cn("bg-emerald-300", row.below_threshold && "bg-amber-300")} style={{ width: `${remainingPct}%` }} />
        </div>
    );
}

/* -----------------------------------------------------------------------
 * ThemeCell — 1 thème par page (3 thèmes réels + 1 page virtuelle "Tous")
 * -----------------------------------------------------------------------
 * Chaque page affiche :
 *   Ligne 1 : [<]  X / Y  [>]
 *   Ligne 2 : Titre du thème · consommation
 *   Ligne 3 : → lien catalogue
 *
 * La dernière page (virtuelle) affiche un grand lien "Toutes les formations".
 * ----------------------------------------------------------------------- */

const MAX_REAL_THEMES = 3;

type ThemeCellProps = {
    themes: CoachFunderTheme[];
    fundingCode: string;
    professionCode: string;
    row: CoachFunderRow;
    basketItems: FundingBasketItem[];
};

function ThemeCell({ themes, fundingCode, professionCode, row, basketItems }: ThemeCellProps) {
    const [page, setPage] = useState(0);

    // Exclure les thèmes dont le produit phare est déjà dans le panier pour ce financeur
    const availableThemes = themes.filter(theme => {
        if (!theme.top_product) return true;
        return !basketItems.some(
            item =>
                item.product_code === theme.top_product!.product_code &&
                item.funding_code.toUpperCase() === fundingCode.toUpperCase()
        );
    });

    if (themes.length === 0) {
        return <span className="text-xs text-gray-400">Aucun thème pour ce mode de financement</span>;
    }

    if (availableThemes.length === 0) {
        return <span className="text-xs text-gray-400">Tous les thèmes sont dans le panier</span>;
    }

    const realCount = Math.min(availableThemes.length, MAX_REAL_THEMES);
    // +1 virtual "all themes" page
    const totalPages = realCount + 1;
    const clampedPage = Math.min(page, totalPages - 1);
    const isVirtualPage = clampedPage === totalPages - 1;
    const theme = isVirtualPage ? null : availableThemes[clampedPage];
    const listingUrl = buildFunderListingUrl(row, professionCode);

    const product = theme?.top_product ?? null;
    // Prefer the pre-computed URL from the backend (always up-to-date, handles
    // the profession correctly). Fall back to client-side construction for
    // payloads generated before crm_catalog_url was added, then to the funder
    // listing URL for synthetic themes that have no ProductTheme row.
    const detailUrl = theme
        ? (theme.crm_catalog_url ?? buildThemeDetailUrl(theme, professionCode, fundingCode) ?? listingUrl)
        : null;
    const consumption = theme ? formatConsumption(theme.consumption, theme.unit) : null;

    return (
        <div className="flex min-w-0 flex-col gap-1">
            {/* navigation */}
            <div className="flex items-center gap-1">
                <button
                    type="button"
                    disabled={clampedPage <= 0}
                    onClick={() => setPage(p => Math.max(0, p - 1))}
                    aria-label="Thème précédent"
                    className={cn(
                        "flex size-4 shrink-0 items-center justify-center rounded hover:bg-gray-100",
                        clampedPage <= 0 ? "cursor-default opacity-25" : "cursor-pointer"
                    )}
                >
                    <ChevronLeft className="size-3" aria-hidden />
                </button>
                <span className="min-w-[28px] text-center text-[10px] tabular-nums text-gray-400">
                    {clampedPage + 1}/{totalPages}
                </span>
                <button
                    type="button"
                    disabled={clampedPage >= totalPages - 1}
                    onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                    aria-label="Thème suivant"
                    className={cn(
                        "flex size-4 shrink-0 items-center justify-center rounded hover:bg-gray-100",
                        clampedPage >= totalPages - 1 ? "cursor-default opacity-25" : "cursor-pointer"
                    )}
                >
                    <ChevronRight className="size-3" aria-hidden />
                </button>
            </div>

            {/* contenu de la page */}
            {isVirtualPage ? (
                /* Page virtuelle : lien vers toutes les formations */
                listingUrl ? (
                    <Link
                        to={listingUrl}
                        className="block rounded-md border border-dashed border-gray-200 px-2 py-1.5 text-center text-xs font-medium text-gray-500 hover:border-blue-300 hover:text-blue-600"
                    >
                        Toutes les formations →
                    </Link>
                ) : (
                    <span className="text-xs text-gray-400">Voir le catalogue</span>
                )
            ) : theme ? (
                /* Page thème réel */
                <>
                    <p className="truncate text-xs font-medium leading-tight text-gray-800" title={theme.theme_title}>
                        {theme.theme_title}
                        {product && consumption ? (
                            <span className="ml-1 font-normal text-gray-400">· {consumption}</span>
                        ) : null}
                    </p>
                    {!theme.fits ? (
                        <p className="text-[10px] font-medium text-amber-600">
                            Dépasse de {formatConsumption(theme.overflow, theme.unit) ?? ""}
                        </p>
                    ) : null}
                    {detailUrl ? (
                        <Link
                            to={detailUrl}
                            className="text-[11px] font-medium text-blue-600 hover:underline"
                        >
                            → Catalogue
                        </Link>
                    ) : null}
                </>
            ) : null}
        </div>
    );
}

/* -----------------------------------------------------------------------
 * Override d'enveloppe (popover discret)
 * ----------------------------------------------------------------------- */

type EnvelopeOverridePopoverProps = {
    row: CoachFunderRow;
    onSetEnvelopeOverride: (input: EnvelopeOverrideInput) => void;
};

function EnvelopeOverridePopover({ row, onSetEnvelopeOverride }: EnvelopeOverridePopoverProps) {
    const [open, setOpen] = useState(false);
    const [mode, setMode] = useState<FundingBasketOverrideMode>(row.override?.mode ?? "remaining");
    const [amount, setAmount] = useState<string>(row.override ? String(row.override.amount) : "");
    const hasOverride = Boolean(row.override);

    const apply = () => {
        const parsed = Number(amount);
        if (Number.isNaN(parsed) || parsed < 0) return;
        onSetEnvelopeOverride({ funding_code: row.funding_code, mode, amount: parsed });
        setOpen(false);
    };

    const reset = () => {
        onSetEnvelopeOverride({ funding_code: row.funding_code, reset: true });
        setAmount("");
        setOpen(false);
    };

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className={cn("size-6 shrink-0", hasOverride ? "text-amber-600" : "text-gray-300 hover:text-gray-500")}
                    aria-label={`Ajuster l'enveloppe ${row.funding_label}`}
                >
                    <SlidersHorizontal className="size-3" aria-hidden />
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-60 space-y-3" align="end">
                <p className="text-xs font-medium text-gray-700">Ajuster l&apos;enveloppe</p>
                <RadioGroup value={mode} onValueChange={v => setMode(v as FundingBasketOverrideMode)} className="grid gap-1.5">
                    <div className="flex items-center gap-2">
                        <RadioGroupItem value="remaining" id={`${row.funding_code}-ov-remaining`} />
                        <Label htmlFor={`${row.funding_code}-ov-remaining`} className="text-xs font-normal">
                            Reste disponible
                        </Label>
                    </div>
                    <div className="flex items-center gap-2">
                        <RadioGroupItem value="consumed" id={`${row.funding_code}-ov-consumed`} />
                        <Label htmlFor={`${row.funding_code}-ov-consumed`} className="text-xs font-normal">
                            Déjà consommé
                        </Label>
                    </div>
                </RadioGroup>
                <Input
                    type="number"
                    min={0}
                    step={row.unit === "hours" ? 0.5 : 1}
                    value={amount}
                    onChange={e => setAmount(e.target.value)}
                    placeholder={row.unit === "hours" ? "Nombre d'heures" : "Montant en €"}
                    className="h-8 text-sm"
                />
                <div className="flex items-center justify-between gap-2">
                    {hasOverride ? (
                        <Button type="button" variant="ghost" size="sm" className="h-7 text-xs text-gray-500" onClick={reset}>
                            Réinitialiser
                        </Button>
                    ) : (
                        <span />
                    )}
                    <Button type="button" size="sm" className="h-7 text-xs" onClick={apply}>
                        Appliquer
                    </Button>
                </div>
            </PopoverContent>
        </Popover>
    );
}
